import {
  $create,
  $getState,
  $setState,
  createState,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  ElementNode,
  LexicalNode,
} from 'lexical';
import checkSvg from '../assets/check.svg';
import xSvg from '../assets/x.svg';

function isAccepted(value: unknown): value is true {
  if (typeof value === 'string') {
    return value === 'true';
  }

  if (value != null) {
    return Boolean(value);
  }

  return false;
}

const inlineSuggestionState = createState<'inline-suggestion-accepted-node', boolean | null>(
  'inline-suggestion-accepted-node',
  {
    parse: value => isAccepted(value),
    unparse: (value: boolean | null) => value,
  },
);

function createSvgElement(svgString: string, size = 16): Element {
  let actualSvgContent = svgString;
  if (svgString.startsWith('data:image/svg+xml,')) {
    actualSvgContent = decodeURIComponent(svgString.replace('data:image/svg+xml,', ''));
  }

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = actualSvgContent;

  const svgElement = tempDiv.querySelector('svg');
  if (!svgElement) {
    throw new Error(
      `Invalid SVG - no SVG element found. Received: ${typeof svgString === 'string' ? svgString.substring(0, 100) : svgString}`,
    );
  }

  svgElement.setAttribute('width', size.toString());
  svgElement.setAttribute('height', size.toString());

  return svgElement;
}

export class InlineSuggestionNode extends ElementNode {
  $config() {
    return this.config('inline-suggestion', {
      extends: ElementNode,
      stateConfigs: [{ flat: true, stateConfig: inlineSuggestionState }],
    });
  }

  static importDOM(): DOMConversionMap | null {
    return {
      'inline-suggestion': () => ({
        conversion: (): DOMConversionOutput => {
          const node = $createInlineSuggestionNode();
          return { node };
        },
        priority: 0,
      }),
    };
  }

  createDOM(): HTMLElement {
    const container = document.createElement('span');
    container.style.backgroundColor = 'var(--mantine-color-success-1)';
    container.style.borderRadius = '4px';
    container.style.padding = '2px 4px';
    container.style.display = 'inline-flex';
    container.style.alignItems = 'center';
    container.style.gap = '4px';
    container.style.border = '1px solid var(--mantine-color-success-3)';
    container.style.verticalAlign = 'baseline';

    const buttonContainer = document.createElement('span');
    buttonContainer.className = 'inline-suggestion-buttons';
    buttonContainer.style.display = 'inline-flex';
    buttonContainer.style.gap = '2px';
    buttonContainer.style.alignItems = 'center';
    buttonContainer.style.marginLeft = '4px';
    buttonContainer.style.order = '2';

    const acceptButton = this.createButton(createSvgElement(checkSvg, 16), 'accept', {
      style: {
        backgroundColor: 'var(--mantine-color-success-6)',
      },
      events: {
        mouseenter: () => {
          acceptButton.style.backgroundColor = 'var(--mantine-color-success-7)';
        },
        mouseleave: () => {
          acceptButton.style.backgroundColor = 'var(--mantine-color-success-6)';
        },
      },
    });

    const rejectButton = this.createButton(createSvgElement(xSvg, 16), 'reject', {
      style: {
        backgroundColor: 'var(--mantine-color-error-6)',
      },
      events: {
        mouseenter: () => {
          rejectButton.style.backgroundColor = 'var(--mantine-color-error-7)';
        },
        mouseleave: () => {
          rejectButton.style.backgroundColor = 'var(--mantine-color-error-6)';
        },
      },
    });

    buttonContainer.appendChild(acceptButton);
    buttonContainer.appendChild(rejectButton);
    container.appendChild(buttonContainer);

    return container;
  }

  updateDOM(prevNode: this, dom: HTMLElement): boolean {
    const currentState = $getState(this, inlineSuggestionState);
    const buttonContainer = dom.querySelector('.inline-suggestion-buttons');

    if (isAccepted(currentState)) {
      if (buttonContainer) {
        (buttonContainer as HTMLElement).style.display = 'none';
      }
      dom.style.backgroundColor = 'transparent';
      dom.style.border = 'none';
      dom.style.padding = '0';
    } else if (currentState === false) {
      dom.style.display = 'none';
    } else {
      if (buttonContainer) {
        (buttonContainer as HTMLElement).style.display = 'inline-flex';
      }
      dom.style.backgroundColor = 'var(--mantine-color-success-1)';
      dom.style.border = '1px solid var(--mantine-color-success-3)';
      dom.style.borderRadius = '4px';
      dom.style.padding = '2px 4px';
      dom.style.display = 'inline-flex';
    }

    return false;
  }

  exportDOM(): DOMExportOutput {
    const currentState = $getState(this, inlineSuggestionState);

    if (!isAccepted(currentState) && currentState !== null) {
      return { element: null };
    }

    const container = document.createElement(
      isAccepted(currentState) ? 'span' : 'inline-suggestion',
    );
    return { element: container };
  }

  private createButton(
    icon: Element,
    intent: 'accept' | 'reject',
    options: {
      style: Record<string, string>;
      events: Record<string, () => void>;
    },
  ): HTMLButtonElement {
    const button = document.createElement('button');
    button.dataset.intent = intent;

    const baseStyles = {
      fontSize: '10px',
      fontWeight: '600',
      height: '20px',
      width: '20px',
      minWidth: '20px',
      color: 'var(--mantine-color-white)',
      border: 'none',
      padding: '0',
      cursor: 'pointer',
      borderRadius: '2px',
      fontFamily: 'Manrope',
      transition: 'background-color 0.2s ease',
      lineHeight: '20px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    };

    Object.assign(button.style, baseStyles);
    Object.assign(button.style, options.style);
    button.appendChild(icon);
    Object.entries(options.events).forEach(([event, handler]) => {
      button.addEventListener(event, handler);
    });

    return button;
  }
}

export function $createInlineSuggestionNode(): InlineSuggestionNode {
  return $setState($create(InlineSuggestionNode), inlineSuggestionState, null);
}

export function $isInlineSuggestionNode(
  node: LexicalNode | null | undefined,
): node is InlineSuggestionNode {
  return node instanceof InlineSuggestionNode;
}

export function $acceptInlineSuggestion(node: InlineSuggestionNode) {
  $setState(node, inlineSuggestionState, true);
}

export function $rejectInlineSuggestion(node: InlineSuggestionNode) {
  $setState(node, inlineSuggestionState, false);
}

export function $isAcceptedInlineSuggestion(node: InlineSuggestionNode) {
  const value = $getState(node, inlineSuggestionState);
  return isAccepted(value);
}

export function $isRejectedInlineSuggestion(node: InlineSuggestionNode) {
  const value = $getState(node, inlineSuggestionState);
  return !isAccepted(value) && value !== null;
}
